import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { canManageAgency, hiddenNavItems, roleLabel, visibleNavItems } from "@/auth/permissions";
import { authApi, ratersApi, settingsApi } from "@/api/endpoints";
import type { IntegrationStatus, MfaSetupResponse, ProgressiveRaterStatus } from "@/api/types";
import {
  formatProgressiveTestResult,
  ProgressiveRaterPanel,
} from "@/components/ProgressiveRaterPanel";
import { PageHeader } from "@/components/PageHeader";

export function SettingsPage() {
  const { user, refreshProfile } = useAuth();
  const [integrations, setIntegrations] = useState<IntegrationStatus | null>(null);
  const [progressive, setProgressive] = useState<ProgressiveRaterStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [mfaSetup, setMfaSetup] = useState<MfaSetupResponse | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaMessage, setMfaMessage] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([settingsApi.integrations(), ratersApi.progressiveStatus()])
      .then(([integrationData, progressiveData]) => {
        setIntegrations(integrationData);
        setProgressive(progressiveData);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load settings"));
  }, []);

  async function runProgressiveTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await ratersApi.testProgressive();
      setTestResult(formatProgressiveTestResult(result));
      const refreshed = await ratersApi.progressiveStatus();
      setProgressive(refreshed);
    } catch (err) {
      setTestResult(`✗ ${err instanceof Error ? err.message : "Test failed"}`);
    } finally {
      setTesting(false);
    }
  }

  async function startMfaSetup() {
    setMfaBusy(true);
    setMfaMessage(null);
    try {
      const result = await authApi.mfaSetup();
      setMfaSetup(result);
    } catch (err) {
      setMfaMessage(err instanceof Error ? err.message : "MFA setup failed");
    } finally {
      setMfaBusy(false);
    }
  }

  async function confirmMfa() {
    if (!mfaCode.trim()) return;
    setMfaBusy(true);
    setMfaMessage(null);
    try {
      await authApi.mfaConfirm(mfaCode.trim());
      setMfaMessage("Multi-factor authentication is now enabled.");
      setMfaSetup(null);
      setMfaCode("");
      await refreshProfile();
    } catch (err) {
      setMfaMessage(err instanceof Error ? err.message : "Invalid MFA code");
    } finally {
      setMfaBusy(false);
    }
  }

  const hidden = hiddenNavItems(user);
  const visible = visibleNavItems(user);

  return (
    <>
      <PageHeader title="Settings" subtitle="Account, permissions, and integration connections." />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="card" style={{ padding: "1rem" }}>
        <h3>Your account</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <div className="label">Name</div>
            <div className="value">{user?.display_name}</div>
          </div>
          <div className="detail-item">
            <div className="label">Email</div>
            <div className="value">{user?.email}</div>
          </div>
          <div className="detail-item">
            <div className="label">Role</div>
            <div className="value">{roleLabel(user)}</div>
          </div>
          <div className="detail-item">
            <div className="label">Agency</div>
            <div className="value">{user?.agency?.display_name ?? "—"}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: "1rem" }}>
        <h3>Subscription & billing</h3>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          Manage your agency plan, seat limits, and carrier rater access.
        </p>
        {canManageAgency(user) ? (
          <Link to="/settings/billing" className="btn btn-primary">
            Open billing
          </Link>
        ) : (
          <p className="empty-state" style={{ padding: "0.5rem 0" }}>
            Contact your agency owner to manage billing.
          </p>
        )}
      </div>

      <div className="card" style={{ padding: "1rem" }}>
        <h3>Data export & migration</h3>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          One-click CSV exports and AMS360 / Applied Epic style importers for clients and policies.
        </p>
        {canManageAgency(user) ? (
          <Link to="/settings/data-migration" className="btn btn-primary">
            Open data migration
          </Link>
        ) : (
          <p className="empty-state" style={{ padding: "0.5rem 0" }}>
            Contact your agency owner to export or import agency data.
          </p>
        )}
      </div>

      <div className="card" style={{ padding: "1rem" }}>
        <h3>Multi-factor authentication</h3>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          Status: {user?.mfa_enabled ? "Enabled" : "Not enabled"}
        </p>
        {!user?.mfa_enabled ? (
          <>
            {!mfaSetup ? (
              <button type="button" className="btn btn-primary" disabled={mfaBusy} onClick={startMfaSetup}>
                {mfaBusy ? "Starting…" : "Set up MFA"}
              </button>
            ) : (
              <div style={{ display: "grid", gap: "1rem", maxWidth: "24rem" }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(mfaSetup.provisioning_uri)}`}
                  alt="MFA QR code"
                  width={180}
                  height={180}
                  style={{ border: "1px solid var(--border)", borderRadius: "8px" }}
                />
                <div>
                  <div className="label" style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                    Manual entry secret
                  </div>
                  <code style={{ wordBreak: "break-all" }}>{mfaSetup.secret}</code>
                </div>
                <label>
                  Confirm with 6-digit code
                  <input
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    inputMode="numeric"
                    maxLength={8}
                    style={{ width: "100%", marginTop: "0.35rem" }}
                  />
                </label>
                <button type="button" className="btn btn-primary" disabled={mfaBusy} onClick={confirmMfa}>
                  {mfaBusy ? "Confirming…" : "Confirm MFA"}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="empty-state" style={{ padding: "0.5rem 0" }}>
            MFA is active on your account. Use your authenticator app at sign-in.
          </p>
        )}
        {mfaMessage ? <p style={{ marginTop: "0.75rem" }}>{mfaMessage}</p> : null}
      </div>

      <ProgressiveRaterPanel
        status={progressive}
        testing={testing}
        testResult={testResult}
        onTest={runProgressiveTest}
      />

      <div className="card" style={{ padding: "1rem" }}>
        <h3>Progressive setup runbook</h3>
        <ol style={{ margin: 0, paddingLeft: "1.25rem", color: "var(--muted)", fontSize: "0.9rem" }}>
          <li>Set <code>ENABLE_PROGRESSIVE_LIVE_RATER=true</code> in your API environment.</li>
          <li>
            For development without credentials, keep <code>PROGRESSIVE_RATER_SANDBOX_MODE=true</code> (default).
          </li>
          <li>
            For production, set <code>PROGRESSIVE_RATER_API_URL</code>, <code>PROGRESSIVE_RATER_API_KEY</code>, and{" "}
            <code>PROGRESSIVE_RATER_AGENCY_CODE</code>, then restart the API.
          </li>
          <li>Use <strong>Test connection</strong> above, then verify quotes on the Rate Shop.</li>
        </ol>
        <Link to="/rate-shop" className="btn btn-secondary" style={{ marginTop: "0.75rem" }}>
          Open rate shop
        </Link>
      </div>

      <div className="card" style={{ padding: "1rem" }}>
        <h3>Navigation access</h3>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          Visible modules: {visible.map((item) => item.label).join(", ")}
        </p>
        {hidden.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Hidden module</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {hidden.map((item) => (
                <tr key={item.path}>
                  <td>{item.label}</td>
                  <td>{item.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty-state">All navigation modules are available for your role.</p>
        )}
      </div>

      {integrations ? (
        <div className="card" style={{ padding: "1rem" }}>
          <h3>All integrations</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(integrations).map(([key, value]) => (
                <tr key={key}>
                  <td>{key.replace(/_/g, " ")}</td>
                  <td>
                    {key === "raters" && "progressive_mode" in value
                      ? String(value.progressive_mode)
                      : "enabled" in value && value.enabled === false
                        ? "Disabled"
                        : "Active"}
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>{JSON.stringify(value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
